import { MouseEventHandler, useCallback } from 'react';
import { Avatar, useChannelActionContext, useChannelStateContext, useChatContext } from 'stream-chat-react';
import { useWorkspaceController } from '../../context/WorkspaceController';

import type { ChannelMemberResponse } from 'stream-chat';
import type { StreamChatType } from '../../types';
import { IconButton, Tooltip, Button, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { InfoOutlined, PushPin } from '@mui/icons-material';

type TeamChannelHeaderProps = {
  popout?: boolean;
};

export const TeamChannelHeader = ({ popout = false }: TeamChannelHeaderProps) => {
  const { displayWorkspace } = useWorkspaceController();
  const { client } = useChatContext<StreamChatType>();
  const { channel, watcher_count } = useChannelStateContext<StreamChatType>();
  const { closeThread } = useChannelActionContext<StreamChatType>();
  const { togglePinnedMessageListOpen } = useWorkspaceController();

  if (!channel) {
    return null;
  }

  // const teamHeader = `# ${channel?.data?.name || channel?.data?.id || 'random'}`;

  const openChannelEditPanel = useCallback(() => {
    displayWorkspace('Admin-Channel-Edit');
  }, [displayWorkspace]);

  const onPinIconClick: MouseEventHandler<HTMLButtonElement> = useCallback((event) => {
    closeThread?.(event);
    togglePinnedMessageListOpen();
  }, [closeThread, togglePinnedMessageListOpen])

  const getMessagingHeader = () => {
    const members = Object.values(channel.state?.members ?? {}) as ChannelMemberResponse<StreamChatType>[];
    const filteredMembers = members.filter(
      ({ user }) => user?.id !== client.userID,
    );
    const additionalMembers = filteredMembers.length - 3;

    if (!filteredMembers.length) {
      return (
        <div className='workspace-header__block'>
          <Avatar image={null} size={32} />
          <p className='team-channel-header__name user'>Johnny Blaze</p>
        </div>
      );
    }

    return (
      <div className='workspace-header__block'>

        {filteredMembers.map(({ user }, i) => {
          if (i > 2) return null;
          return (
            <ListItem dense key={user?.id ?? `member-${i}`}>
              <ListItemAvatar>
                <Avatar image={user?.image} name={user?.name || user?.id} size={40} />
              </ListItemAvatar>
              <ListItemText primary={user?.name || user?.id || 'Johnny Blaze'} />
            </ListItem>
          );
        })}
        {additionalMembers > 0 && (
          <ListItem dense>
            <ListItemText primary={`and ${additionalMembers} more`} />
          </ListItem>
        )}
      </div>
    );
  };

  const getWatcherText = (watchers?: number) => {
    if (!watchers) return 'No users online';
    if (watchers === 1) return '1 user online';
    return `${watchers} users online`;
  };

  const channelName = (channel.data?.name as string | undefined)
    || (channel.data?.id as string | undefined)
    || 'Channel';
  const channelImage = channel.data?.image as string | undefined;

  return (
    <div className='team-channel-header__container'>
      {channel.type === 'messaging' ? (
        getMessagingHeader()
      ) : (
        <div className='workspace-header__block'>
          <ListItem>
            <ListItemAvatar>
              <Avatar image={channelImage} name={channelName} size={40} />
            </ListItemAvatar>
            <ListItemText primary={channelName} primaryTypographyProps={{ variant: 'h5', maxWidth: popout ? 250 : 'inherit' }} />
          </ListItem>
        </div>
      )}
      {
        !popout && (
          <div className='workspace-header__block'>
            <div className='workspace-header__subtitle'>{getWatcherText(watcher_count)}</div>
            <Button variant="outlined" endIcon={<PushPin />} color="secondary" onClick={onPinIconClick}>
              Pins
            </Button>
            <Tooltip title="Channel Information">
              <IconButton color="primary" onClick={openChannelEditPanel}>
                <InfoOutlined color="primary" />
              </IconButton>
            </Tooltip>
          </div>
        )
      }

    </div>
  );
};
